import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { api, apiUpload, fileUrl } from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import FavPinButtons from "@/components/FavPinButtons";
import RowActions from "@/components/RowActions";
import SwipeableRow from "@/components/SwipeableRow";
import VoiceRecorder from "@/components/VoiceRecorder";
import { toast } from "sonner";
import { swallow } from "@/lib/log";
import { cn } from "@/lib/utils";
import { Mic, Plus, Play, Pause, Sparkles, Loader2 } from "lucide-react";

function fmtDuration(s) {
  if (!s && s !== 0) return "";
  const n = Math.max(0, Math.floor(s));
  const m = Math.floor(n / 60);
  const r = n % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

export default function VoiceNotes() {
  const [items, setItems] = useState([]);
  const [recordOpen, setRecordOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [playing, setPlaying] = useState(null); // id currently playing
  const [improving, setImproving] = useState(null);
  const [editingTranscript, setEditingTranscript] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [transcriptDraft, setTranscriptDraft] = useState("");
  const [titleDraft, setTitleDraft] = useState("");
  const audioEls = useRef({});
  const [sp, setSp] = useSearchParams();

  const load = async () => {
    try {
      const { data } = await api.get("/voice-notes");
      setItems(data || []);
    } catch (e) { swallow("VoiceNotes")(e); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (sp.get("new") === "1") {
      setRecordOpen(true);
      const next = new URLSearchParams(sp);
      next.delete("new");
      setSp(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [sp]);

  const onSaved = (note) => {
    setRecordOpen(false);
    setItems((arr) => [note, ...arr]);
  };

  // RowActions renders the delete-with-undo toast; here we just do the API + UI update.
  const del = async (n) => {
    setItems((arr) => arr.filter((x) => x.id !== n.id));
    try { await api.delete(`/voice-notes/${n.id}`); }
    catch { toast.error("Delete failed — restored"); load(); }
  };

  const togglePlay = (n) => {
    const el = audioEls.current[n.id];
    if (!el) return;
    // Pause any others
    Object.entries(audioEls.current).forEach(([id, other]) => {
      if (id !== n.id) { try { other.pause(); } catch {} }
    });
    if (playing === n.id) {
      el.pause();
      setPlaying(null);
    } else {
      el.play();
      setPlaying(n.id);
    }
  };

  const improveTranscript = async () => {
    if (!detail) return;
    setImproving(detail.id);
    try {
      const { data } = await api.post(`/voice-notes/${detail.id}/improve-transcript`);
      setDetail(data);
      setItems((arr) => arr.map((x) => x.id === data.id ? data : x));
      toast.success("Transcript improved with Whisper");
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || "Improve failed";
      if (status === 402) {
        toast.error(msg, {
          action: { label: "Add key", onClick: () => { window.location.href = "/app/profile"; } },
        });
      } else {
        toast.error(msg);
      }
    } finally {
      setImproving(null);
    }
  };

  const saveTranscriptEdit = async () => {
    if (!detail) return;
    try {
      const { data } = await api.patch(`/voice-notes/${detail.id}`, { transcript: transcriptDraft });
      setDetail(data);
      setItems((arr) => arr.map((x) => x.id === data.id ? data : x));
      setEditingTranscript(false);
      toast.success("Transcript updated");
    } catch { toast.error("Update failed"); }
  };

  const saveTitleEdit = async () => {
    if (!detail) return;
    try {
      const { data } = await api.patch(`/voice-notes/${detail.id}`, { title: titleDraft });
      setDetail(data);
      setItems((arr) => arr.map((x) => x.id === data.id ? data : x));
      setEditingTitle(false);
      toast.success("Title updated");
    } catch { toast.error("Update failed"); }
  };

  return (
    <>
      <TopBar
        title="Voice notes"
        subtitle="Speak, transcribe, recall"
        back
        showSearch
        right={
          <button
            onClick={() => setRecordOpen(true)}
            className="h-9 w-9 rounded-full bg-primary text-primary-foreground grid place-items-center"
            aria-label="New voice note"
            data-testid="voice-notes-add-button"
          >
            <Plus className="h-4 w-4" />
          </button>
        }
      />

      <div className="px-4 py-4 space-y-3" data-testid="voice-notes-page">
        {items.length === 0 && (
          <EmptyState
            icon={Mic}
            title="No voice notes yet"
            subtitle="Tap + to record your first voice note — it'll be transcribed automatically."
          />
        )}

        {items.map((n) => (
          <SwipeableRow
            key={n.id}
            testId={`swipe-voice-${n.id}`}
            onSwipeLeft={async () => {
              setItems((arr) => arr.filter((x) => x.id !== n.id));
              try {
                await api.post("/archive/toggle", { type: "voice_note", id: n.id, archived: true });
                toast.success("Voice note archived");
              } catch { toast.error("Archive failed"); load(); }
            }}
            onSwipeRight={async () => {
              const next = !n.is_favorite;
              setItems((arr) => arr.map((x) => x.id === n.id ? { ...x, is_favorite: next } : x));
              try {
                await api.post("/favorites/toggle", { type: "voice_note", id: n.id, is_favorite: next });
                toast.success(next ? "Added to Favorites" : "Removed from Favorites");
              } catch { toast.error("Update failed"); load(); }
            }}
          >
            <div className="rounded-2xl border border-border bg-card p-3" data-testid="voice-note-row">
              <div className="flex items-start gap-3">
                <button
                  onClick={() => togglePlay(n)}
                  className={cn(
                    "h-10 w-10 rounded-xl grid place-items-center shrink-0 transition",
                    playing === n.id ? "bg-primary text-primary-foreground" : "bg-primary/12 text-primary hover:bg-primary/20",
                  )}
                  aria-label={playing === n.id ? "Pause" : "Play"}
                  data-testid={`voice-note-play-${n.id}`}
                >
                  {playing === n.id ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                </button>
                <button
                  onClick={() => { setDetail(n); setTranscriptDraft(n.transcript || ""); setTitleDraft(n.title || ""); }}
                  className="flex-1 text-left min-w-0"
                >
                  <div className="flex items-center gap-1.5">
                    <div className="font-display font-semibold text-[14px] leading-tight truncate">{n.title}</div>
                    {n.active_transcript === "whisper" && (
                      <Badge variant="secondary" className="h-4 px-1.5 rounded-full text-[9px] uppercase tracking-wider">Whisper</Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                    {n.transcript ? n.transcript : "No transcript yet"}
                  </div>
                  <div className="text-[10px] text-muted-foreground/80 truncate mt-0.5">
                    {fmtDuration(n.duration_seconds)} · {fmtWhen(n.created_at)}
                  </div>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  <FavPinButtons
                    type="voice_note"
                    id={n.id}
                    isFavorite={!!n.is_favorite}
                    pinnedAt={n.pinned_at}
                    size="sm"
                    onChanged={(u) => setItems((arr) => arr.map((x) => x.id === n.id ? { ...x, ...u } : x))}
                    onArchived={(id) => setItems((arr) => arr.filter((x) => x.id !== id))}
                  />
                  <RowActions
                    size="sm"
                    testId={`voice-${n.id}`}
                    deleteLabel="Delete voice note"
                    onDelete={() => del(n)}
                  />
                </div>
              </div>
              <audio
                ref={(el) => { if (el) audioEls.current[n.id] = el; }}
                src={n.file_url ? fileUrl(n.file_url) : undefined}
                preload="none"
                onEnded={() => setPlaying(null)}
                className="hidden"
              />
            </div>
          </SwipeableRow>
        ))}
      </div>

      {/* Recorder dialog */}
      <Dialog open={recordOpen} onOpenChange={setRecordOpen}>
        <DialogContent className="rounded-2xl max-w-[92vw] md:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">New voice note</DialogTitle>
          </DialogHeader>
          <VoiceRecorder
            onSaved={onSaved}
            onCancel={() => setRecordOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) { setDetail(null); setEditingTranscript(false); setEditingTitle(false); } }}>
        <DialogContent className="rounded-2xl max-w-[92vw] md:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              {editingTitle ? (
                <Input
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  className="h-9 text-sm"
                  data-testid="voice-note-title-input"
                />
              ) : (
                <span className="truncate">{detail?.title}</span>
              )}
            </DialogTitle>
          </DialogHeader>

          {detail && (
            <div className="space-y-3">
              {editingTitle ? (
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={saveTitleEdit} data-testid="voice-note-title-save">Save title</Button>
                  <Button size="sm" variant="outline" onClick={() => { setEditingTitle(false); setTitleDraft(detail.title || ""); }}>Cancel</Button>
                </div>
              ) : (
                <button className="text-[11px] text-primary underline" onClick={() => setEditingTitle(true)} data-testid="voice-note-title-edit">Rename</button>
              )}

              {/* Playback */}
              <div className="rounded-xl border border-border bg-card p-3 flex items-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => togglePlay(detail)}
                  data-testid="voice-note-detail-play"
                >
                  {playing === detail.id ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </Button>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-muted-foreground">Duration · {fmtDuration(detail.duration_seconds)}</div>
                  <div className="text-[10px] text-muted-foreground/80">Recorded {fmtWhen(detail.created_at)}</div>
                </div>
                <audio
                  ref={(el) => { if (el) audioEls.current[detail.id] = el; }}
                  src={detail.file_url ? fileUrl(detail.file_url) : undefined}
                  preload="metadata"
                  onEnded={() => setPlaying(null)}
                  className="hidden"
                />
              </div>

              {/* Transcript */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                    Transcript {detail.active_transcript === "whisper" && <Badge variant="secondary" className="ml-1 h-4 px-1 rounded-full text-[8px]">Whisper</Badge>}
                  </div>
                  <div className="flex items-center gap-1">
                    {!editingTranscript && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setEditingTranscript(true); setTranscriptDraft(detail.transcript || ""); }}
                        data-testid="voice-note-transcript-edit"
                      >
                        Edit
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={improveTranscript}
                      disabled={improving === detail.id}
                      className="gap-1.5"
                      data-testid="voice-note-improve"
                    >
                      {improving === detail.id ? (
                        <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Improving…</>
                      ) : (
                        <><Sparkles className="h-3.5 w-3.5" /> Improve</>
                      )}
                    </Button>
                  </div>
                </div>
                {editingTranscript ? (
                  <>
                    <Textarea
                      rows={6}
                      value={transcriptDraft}
                      onChange={(e) => setTranscriptDraft(e.target.value)}
                      data-testid="voice-note-transcript-input"
                    />
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={saveTranscriptEdit} data-testid="voice-note-transcript-save">Save transcript</Button>
                      <Button size="sm" variant="outline" onClick={() => { setEditingTranscript(false); setTranscriptDraft(detail.transcript || ""); }}>Cancel</Button>
                    </div>
                  </>
                ) : (
                  <div className="text-sm whitespace-pre-wrap break-words" data-testid="voice-note-transcript-view">
                    {detail.transcript || <span className="text-muted-foreground italic">No transcript. Tap Improve to generate one with Whisper.</span>}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setDetail(null); setEditingTranscript(false); setEditingTitle(false); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
